import React from 'react';

import { useQuery } from '@apollo/client/react';
import GET_SMS_REQUESTS_FOR_ACCOUNT_Q from '@/features/messaging/gql/getSmsRequestsForAccount.gql';
import DIDS_SHOW_Q from '../gql/show.gql';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { MainView } from '@repo/ui/Views';
import { POLICIES } from '@/lib/Authorization';
import { z } from 'zod';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import type { Mercury } from '@repo/types/Models';
import Conversation from '../components/Conversations/Conversation';
import { useAccount } from '@/hooks/useAccount';
import { useUser } from '@repo/hooks/useUser';
import DidTabNav from '../components/DidTabNav';

import ConversationFilter from '../components/Conversations/ConversationFilter';
import { Form } from '@repo/ui/Form';
import { useParams } from 'react-router';

const SMS_REQUESTS_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  remote_number: z
    .string()
    .min(10, { message: 'Remote Number must be at least 10 digits' })
    .default(''),
});

const DidConversations = () => {
  const account = useAccount();
  const user = useUser();
  const accountId = account?.id;

  const params = useParams();
  const didId = params.didId;

  const {
    loading: didLoading,
    data: didData,
    refetch: didRefetch,
  } = useQuery(DIDS_SHOW_Q, {
    variables: {
      id: String(didId),
    },
  });

  const PAGE_SIZE = 10;

  const canViewSmsRequests = POLICIES['sms_requests:show'](user);

  const filter = useZodQuery({
    schema: SMS_REQUESTS_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, ...queryFields } = filter.queryValues;
  const hasRemoteNumber = queryFields.remote_number && queryFields.remote_number.length >= 10;

  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
        host_number: didData?.did?.number,
      },
      coerceValue
    ),
  ];

  const { loading, error, data } = useQuery(GET_SMS_REQUESTS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    skip: !hasRemoteNumber,
    notifyOnNetworkStatusChange: true,
  });
  if (error) {
    console.error(error);
  }

  const { edges: smsRequestsNodes, pageInfo } = data?.smsRequestsForAccount ?? {};

  const nodes =
    smsRequestsNodes?.map((smsRequest: { node: Mercury.Models.SMS }) => smsRequest.node) || [];

  //13503801585

  return (
    <MainView title={didLoading ? `Loading...` : `${didData?.did?.number}`}>
      <DidTabNav />
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <ConversationFilter queryList={queryList} />
      </Form>
      <Conversation nodes={nodes} />
    </MainView>
  );
};

export default DidConversations;
